import React, { useState, useEffect } from 'react';
import { getArticles, createArticle, updateArticle, deleteArticle, adjustArticleStock } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Package,
  Plus,
  Search,
  Edit2,
  Trash2,
  Euro,
  Upload,
  Barcode,
  AlertTriangle,
  ArrowUpDown,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import PageHelpButton from '../components/help/PageHelpButton';

const formatCurrency = (amount) => new Intl.NumberFormat('es-ES', { useGrouping: 'always', style: 'currency', currency: 'EUR' }).format(amount || 0);

const emptyForm = {
  name: '',
  description: '',
  sku: '',
  barcode: '',
  unit_price: '',
  tax_rate: '21',
  unit: 'unidad',
  stock_control: false,
  stock: '0',
  min_stock: '0',
  category: '',
};

export const ArticlesPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [stockDialog, setStockDialog] = useState({ open: false, article: null, delta: '', reason: '' });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const { data } = await getArticles();
      setArticles(data);
    } catch (e) {
      toast.error('Error al cargar artículos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        unit_price: parseFloat(formData.unit_price) || 0,
        tax_rate: parseFloat(formData.tax_rate) || 21,
        stock: parseFloat(formData.stock) || 0,
        min_stock: parseFloat(formData.min_stock) || 0,
        sku: formData.sku || null,
        barcode: formData.barcode || null,
        description: formData.description || null,
        category: formData.category || null,
      };
      if (editing) {
        await updateArticle(editing.id, payload);
        toast.success('Artículo actualizado');
      } else {
        await createArticle(payload);
        toast.success('Artículo creado');
      }
      load();
      closeModal();
    } catch (e) {
      toast.error('Error al guardar artículo');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este artículo?')) return;
    try {
      await deleteArticle(id);
      toast.success('Artículo eliminado');
      load();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const openModal = (article = null) => {
    if (article) {
      setEditing(article);
      setFormData({
        name: article.name || '',
        description: article.description || '',
        sku: article.sku || '',
        barcode: article.barcode || '',
        unit_price: article.unit_price?.toString() || '',
        tax_rate: (article.tax_rate ?? 21).toString(),
        unit: article.unit || 'unidad',
        stock_control: !!article.stock_control,
        stock: (article.stock ?? 0).toString(),
        min_stock: (article.min_stock ?? 0).toString(),
        category: article.category || '',
      });
    } else {
      setEditing(null);
      setFormData(emptyForm);
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditing(null);
  };

  const submitStockAdjust = async () => {
    const delta = parseFloat(stockDialog.delta);
    if (!delta || isNaN(delta)) {
      toast.error('Introduce un valor distinto de cero');
      return;
    }
    try {
      await adjustArticleStock(stockDialog.article.id, delta, stockDialog.reason || 'ajuste_manual');
      toast.success('Stock actualizado');
      setStockDialog({ open: false, article: null, delta: '', reason: '' });
      load();
    } catch {
      toast.error('Error al ajustar stock');
    }
  };

  const filtered = articles.filter(a =>
    a.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.barcode?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B4A]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight">Artículos</h1>
            <p className="text-stone-500 text-sm mt-1">Productos físicos con referencia, stock y precio</p>
          </div>
          <PageHelpButton moduleKey="articles" variant="icon" />
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <Link to="/import?entity=articles">
            <Button variant="outline" className="rounded-xl border-emerald-200 text-emerald-700 hover:bg-emerald-50 w-full sm:w-auto" data-testid="import-articles-button">
              <Upload className="w-4 h-4 mr-2" />
              Importar Excel
            </Button>
          </Link>
          <Button
            onClick={() => openModal()}
            className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300 w-full sm:w-auto"
            data-testid="add-article-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Artículo
          </Button>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
        <Input
          placeholder="Buscar por nombre, SKU o código..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 rounded-xl"
          data-testid="search-articles-input"
        />
      </div>

      {filtered.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((article, index) => {
            const lowStock = article.stock_control && article.stock <= article.min_stock;
            return (
              <div
                key={article.id}
                className={`bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-5 card-hover animate-fade-in-up stagger-${(index % 5) + 1}`}
                data-testid={`article-card-${article.id}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 bg-violet-50 rounded-xl flex items-center justify-center shrink-0">
                      <Package className="w-5 h-5 text-violet-600" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-medium text-stone-900 truncate">{article.name}</h3>
                      {article.sku && (
                        <div className="flex items-center gap-1 text-xs text-stone-500">
                          <Barcode className="w-3 h-3" />
                          <span className="truncate">{article.sku}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {article.stock_control && (
                      <button
                        onClick={() => setStockDialog({ open: true, article, delta: '', reason: '' })}
                        className="p-1.5 hover:bg-violet-50 rounded-xl transition-colors"
                        title="Ajustar stock"
                        data-testid={`stock-article-${article.id}`}
                      >
                        <ArrowUpDown className="w-4 h-4 text-violet-600" />
                      </button>
                    )}
                    <button onClick={() => openModal(article)} className="p-1.5 hover:bg-stone-100 rounded-xl transition-colors" data-testid={`edit-article-${article.id}`}>
                      <Edit2 className="w-4 h-4 text-stone-500" />
                    </button>
                    <button onClick={() => handleDelete(article.id)} className="p-1.5 hover:bg-red-50 rounded-xl transition-colors" data-testid={`delete-article-${article.id}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </div>

                {article.description && (
                  <p className="text-sm text-stone-600 mb-4 line-clamp-2">{article.description}</p>
                )}

                <div className="flex items-center justify-between pt-3 border-t border-stone-100">
                  <span className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">
                    {article.stock_control ? 'Stock' : 'Precio'}
                  </span>
                  <div className="flex items-center gap-2">
                    {article.stock_control && (
                      <span className={`text-sm font-mono font-semibold ${lowStock ? 'text-red-600' : 'text-stone-700'}`}>
                        {lowStock && <AlertTriangle className="w-3 h-3 inline mr-1" />}
                        {article.stock} {article.unit}
                      </span>
                    )}
                    <span className="text-lg font-mono font-semibold text-[#FF6B4A]">
                      {formatCurrency(article.unit_price)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100">
          <Package className="w-12 h-12 mx-auto mb-4 text-stone-300" />
          <h3 className="text-lg font-medium text-stone-900 mb-1">No hay artículos</h3>
          <p className="text-stone-500 mb-4">Productos físicos que vendes con referencia y precio</p>
          <Button onClick={() => openModal()} className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full" data-testid="add-first-article-button">
            <Plus className="w-4 h-4 mr-2" />
            Añadir Artículo
          </Button>
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editing ? 'Editar Artículo' : 'Nuevo Artículo'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre del Artículo *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="rounded-xl"
                placeholder="Ej: Camiseta talla M, Tornillo M8..."
                data-testid="article-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sku">SKU / Referencia</Label>
                <Input
                  id="sku"
                  value={formData.sku}
                  onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                  className="rounded-xl font-mono"
                  placeholder="ART-001"
                  data-testid="article-sku-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="barcode">Código de barras</Label>
                <Input
                  id="barcode"
                  value={formData.barcode}
                  onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                  className="rounded-xl font-mono"
                  placeholder="8400000000000"
                  data-testid="article-barcode-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="rounded-xl resize-none"
                rows={2}
                data-testid="article-description-input"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="unit_price">Precio *</Label>
                <div className="relative">
                  <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <Input
                    id="unit_price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.unit_price}
                    onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
                    required
                    className="pl-10 rounded-xl font-mono"
                    data-testid="article-price-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="tax_rate">IVA %</Label>
                <Input
                  id="tax_rate"
                  type="number"
                  step="1"
                  min="0"
                  value={formData.tax_rate}
                  onChange={(e) => setFormData({ ...formData, tax_rate: e.target.value })}
                  className="rounded-xl font-mono"
                  data-testid="article-tax-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="unit">Unidad</Label>
                <Select value={formData.unit} onValueChange={(v) => setFormData({ ...formData, unit: v })}>
                  <SelectTrigger className="rounded-xl" data-testid="article-unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unidad">Unidad</SelectItem>
                    <SelectItem value="caja">Caja</SelectItem>
                    <SelectItem value="kg">Kg</SelectItem>
                    <SelectItem value="g">g</SelectItem>
                    <SelectItem value="m">m</SelectItem>
                    <SelectItem value="m2">m²</SelectItem>
                    <SelectItem value="l">L</SelectItem>
                    <SelectItem value="pack">Pack</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">Categoría</Label>
              <Input
                id="category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="rounded-xl"
                placeholder="Ej: Textil, Ferretería, Bebidas..."
                data-testid="article-category-input"
              />
            </div>

            <div className="border-t border-stone-100 pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="stock_control" className="cursor-pointer">Controlar stock</Label>
                  <p className="text-xs text-stone-500">Activar para gestionar existencias</p>
                </div>
                <Switch
                  id="stock_control"
                  checked={formData.stock_control}
                  onCheckedChange={(checked) => setFormData({ ...formData, stock_control: checked })}
                  data-testid="article-stock-control-switch"
                />
              </div>

              {formData.stock_control && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="stock">Stock actual</Label>
                    <Input
                      id="stock"
                      type="number"
                      step="0.01"
                      value={formData.stock}
                      onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                      className="rounded-xl font-mono"
                      data-testid="article-stock-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="min_stock">Stock mínimo (aviso)</Label>
                    <Input
                      id="min_stock"
                      type="number"
                      step="0.01"
                      value={formData.min_stock}
                      onChange={(e) => setFormData({ ...formData, min_stock: e.target.value })}
                      className="rounded-xl font-mono"
                      data-testid="article-minstock-input"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={closeModal} className="flex-1 rounded-xl">
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full" data-testid="save-article-button">
                {editing ? 'Guardar' : 'Crear'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Stock Adjustment Dialog */}
      <Dialog open={stockDialog.open} onOpenChange={(open) => !open && setStockDialog({ open: false, article: null, delta: '', reason: '' })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Ajustar stock</DialogTitle>
          </DialogHeader>
          {stockDialog.article && (
            <div className="space-y-4">
              <div className="p-3 bg-stone-50 rounded-xl">
                <p className="text-sm text-stone-600">{stockDialog.article.name}</p>
                <p className="text-xs text-stone-500">Stock actual: <span className="font-mono font-semibold">{stockDialog.article.stock} {stockDialog.article.unit}</span></p>
              </div>
              <div className="space-y-2">
                <Label>Cambio (+ entrada / − salida)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={stockDialog.delta}
                  onChange={(e) => setStockDialog({ ...stockDialog, delta: e.target.value })}
                  placeholder="Ej: 10 o -3"
                  className="rounded-xl font-mono"
                  data-testid="stock-delta-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Motivo (opcional)</Label>
                <Input
                  value={stockDialog.reason}
                  onChange={(e) => setStockDialog({ ...stockDialog, reason: e.target.value })}
                  placeholder="Compra, inventario, rotura..."
                  className="rounded-xl"
                  data-testid="stock-reason-input"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setStockDialog({ open: false, article: null, delta: '', reason: '' })} className="flex-1 rounded-xl">
                  Cancelar
                </Button>
                <Button onClick={submitStockAdjust} className="flex-1 bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full" data-testid="confirm-stock-adjust">
                  Aplicar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ArticlesPage;
